import React, {
  ChangeEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  Box,
  InputLabel,
  Checkbox,
  ListSubheader,
} from '@mui/material';
import Select, {SelectChangeEvent, SelectProps} from '@mui/material/Select';
import {FixedSizeList, ListChildComponentProps} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {styled} from '@mui/material/styles';

import AurSelectedItemsPanel from './AurSelectedItemsPanel';
import AurButton from '../aur-button/AurButton';
import SelectItem from './SelectItem';
import LoadingItem from './LoadingItem';
import {ISelectItem} from './types';
import {ReactComponent as ExpandIcon} from '../../../assests/svg/down-triangle.svg';

import {t} from 'i18next';
import styles from './style.module.scss';
import AurSearchBox from '../aur-search-box/AurSearchBox';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';
import {isBoolean} from 'lodash';
const SCROLL_BAR_STYLE_CLASS = 'aur-item-container-scroll'; // Scrollbar selector class
const NO_RESULTS = 'No Results';
const DEFAULT_MUI_BORDER = 2; // default border (top + bottom) for dropdown menu provided by the MUI Library
const MUI_CANVAS_ADJUSTMENT = 44; // default bottom-padding applied by MUI Library
const CANVAS_UPPER_ADJUSTMENT = 8; // adjust the dropdown position to upper direction by addning extra padding/margin

// FormControl fixed style
const StyledFormControl = styled(FormControl)(() => ({
  '& .MuiSelect-filled': {
    backgroundColor: 'transparent',
    border: 'none',
  },
}));

type AurSelectChangeEvent<Value = string> =
  | (Event & {
      target: {
        value: Value;
        name: string;
      };
    })
  | ChangeEvent<HTMLInputElement>;

type AurSelectDropdownProps = {
  items: ISelectItem[]; // All options/items
  itemKey: string; // Name of the unique property of item/option object
  itemDisplay: string; // Name of the "display text in options" property of item/option object
  dropdownLabel?: string; // Label of the dropdown
  search?: boolean;
  searchPlaceHolder?: string; // Search field place holder
  selectAll?: boolean;
  selectAllLabel?: string; // Select all checkbox label
  defaultSelection?: ISelectItem[]; // Default selected items/options
  itemsCountwithNoScroll?: number; // Minimum items/option shows with no scrollbar appear
  submitButtonText?: string;
  disabled?: boolean;
  helperTextClass?: string;
  showCheckIcon?: boolean;
  maxDropdownItemLength?: number | null; //Show ellipsis after x characters in dropdown item
  dismissDropdownWhenClear?: boolean;
  menuClass?: string;
  itemHeight?: number; // Item height
  numberOfVisibleItem?: number; // default 3, visible item in list

  // For state
  field?: {
    value: ISelectItem[] | undefined;
    onChange: (event: SelectChangeEvent<string[] | unknown>) => void;
    onBlur: () => void;
  };
  fieldState?: {
    error?: {
      message?: string;
    };
  };

  // Extra footer
  renderFooter?: React.ReactNode;

  // For validation
  hasValidationError?: boolean;
  validationText?: string;
  helperText?: string;
  labelId?: string;
  dataTestId?: string;
  onSelectionChange: (selectedItems?: ISelectItem[]) => void; // Called after items/options commit/add
  onClearClicked?: () => void; // Called after clearing all selected items/options
  onOpenDropdown?: () => void; // Called after dropdown onen
  onCloseDropdown?: () => void; // Called after dropdown close
  clearValues?: boolean;
  hintText?: string;

  // Server-side processing
  itemsPerPage: number;
  onPage?: (startIndex: number, stopIndex: number) => Promise<ISelectItem[]>;
  onSearch?: (keyword: string) => Promise<ISelectItem[]>;
  hasNextPage: boolean;
  isLoading?: boolean;
} & SelectProps;

/**
 * Custom multi select dropdown
 *
 * Important: the container around this component must have >= 16px left padding/margin [mui suggested standard]
 */
const AurServerSelect = (props: AurSelectDropdownProps) => {
  const {
    items,
    itemKey,
    itemDisplay,
    dropdownLabel,
    search = true,
    selectAll = true,
    selectAllLabel = t('common.selectAll'),
    defaultSelection = [],
    submitButtonText = t('buttons.apply'),
    disabled = false,
    helperTextClass,
    dismissDropdownWhenClear = false,
    itemHeight = 56,
    numberOfVisibleItem = 3,

    field,
    fieldState,

    hasValidationError = false,
    validationText = '',
    helperText = '',
    labelId,
    dataTestId,
    onSelectionChange,
    onClearClicked,
    onOpenDropdown,
    onCloseDropdown,
    onSearch,
    clearValues = false,
    showCheckIcon = true,
    hintText,
    itemsPerPage,
    onPage,
    hasNextPage,
    maxDropdownItemLength,
    renderFooter,
    menuClass = '',
    isLoading,
    ...rest
  } = props;

  const [searchResults, setSearchResults] = useState<ISelectItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [itemData, setItemData] = useState<ISelectItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<ISelectItem[]>([]);
  const [selectedItemsTemp, setSelectedItemsTemp] = useState<ISelectItem[]>([]);
  const [allSelect, setAllSelect] = useState<'check' | 'partialCheck' | ''>('');
  const [showSelectAllCheckbox, setShowSelectAllCheckbox] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [mouseEnter, setMouseEnter] = useState(false);
  const [menuPosition, setMenuPosition] = useState(0);
  const selectComponentRef = useRef<HTMLInputElement | null>(null);
  const popUpContainerRef = useRef<HTMLDivElement | null>(null);
  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: styles.tabFocusedBorder,
    mouseFocusClass: styles.mouseFocusedBorder,
  });
  const [calculatedDropdownHeight, setCalculatedDropdownHeight] =
    useState<number>(0);

  const conditionalPositioningProp =
    menuPosition < 0
      ? {marginTop: `${menuPosition}px`}
      : {paddingTop: `${menuPosition}px`};

  // Observe window resize
  useEffect(() => {
    // Run on mount and scroll/resize
    window.addEventListener('scroll', calculateDirection, true);
    window.addEventListener('resize', calculateDirection);

    return () => {
      window.removeEventListener('scroll', calculateDirection, true);
      window.removeEventListener('resize', calculateDirection);
    };
  }, []);

  const calculateDirection = () => {
    if (!selectComponentRef.current || !popUpContainerRef.current) return;

    const rect = selectComponentRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow =
      window.innerHeight -
      rect.bottom -
      selectComponentRef.current.clientHeight;

    const dropdownHeight =
      (popUpContainerRef.current.parentElement?.clientHeight ?? 0) +
      DEFAULT_MUI_BORDER;

    const selectComponentHeight =
      selectComponentRef.current.clientHeight + DEFAULT_MUI_BORDER;

    if (popUpContainerRef.current.clientHeight) {
      setCalculatedDropdownHeight(
        dropdownHeight + selectComponentHeight + MUI_CANVAS_ADJUSTMENT
      );
    }

    if (spaceBelow >= spaceAbove || spaceBelow >= dropdownHeight) {
      setMenuPosition(selectComponentHeight);
    } else {
      setMenuPosition((dropdownHeight + CANVAS_UPPER_ADJUSTMENT) * -1);
    }
  };

  const hintId = useId();

  // MenuProps fixed style
  const MenuProps = {
    PaperProps: {
      classes: {
        root: `${styles.multiSelectDropdownMenu} ${menuClass}`,
      },
    },
    disableScrollLock: true, // Prevent the scrollbar from disappearing
  };

  useEffect(() => {
    if (isSearching) loadSearchResults(searchTerm);
  }, [isSearching, searchTerm]);

  // create new custom event
  const createSelectChangeEvent = (
    Value: ISelectItem[],
    Name?: string
  ): AurSelectChangeEvent<ISelectItem[]> => {
    return {
      target: {
        value: Value,
        name: Name,
      },
      type: 'SelectChangeEvent',
      currentTarget: null,
      bubbles: false,
      cancelable: false,
      timeStamp: Date.now(),
    } as AurSelectChangeEvent<ISelectItem[]>;
  };

  // Called for temporary selected/unselected
  const selectionChangeHandler = (data: ISelectItem) => {
    const targetItem = selectedItemsTemp.find(
      (item) => item[itemKey] == data[itemKey]
    );
    setIsExpanded(false);
    if (targetItem) {
      setSelectedItemsTemp((pv) => {
        return [...pv.filter((item) => item[itemKey] != data[itemKey])];
      });
    } else {
      setSelectedItemsTemp((pv) => {
        return [...pv, data];
      });
    }
  };

  // Called for "Clear All" button
  const clearAllHandler = () => {
    const event: AurSelectChangeEvent<ISelectItem[]> = createSelectChangeEvent(
      []
    );

    field?.onChange(event);

    setSelectedItemsTemp([]);
    setSelectedItems([]);
    setAllSelect('');
    if (dismissDropdownWhenClear) setIsDropdownOpen(false);
    if (onClearClicked) onClearClicked();
  };

  // Called for "Add" button
  const addClickHandler = () => {
    const event: AurSelectChangeEvent<ISelectItem[]> = createSelectChangeEvent([
      ...selectedItemsTemp,
    ]);

    field?.onChange(event);

    setSelectedItems([...selectedItemsTemp]);
    onSelectionChange([...selectedItemsTemp]);
    setIsDropdownOpen(false);
  };

  // Called after dropdown open
  const selectOpenHandler = () => {
    setIsDropdownOpen(true);
    setSelectedItemsTemp([...selectedItems]);
    if (onOpenDropdown) onOpenDropdown();
  };

  // Called after dropdown close
  const selectCloseHandler = () => {
    setIsDropdownOpen(false);
    if (onCloseDropdown) onCloseDropdown();
  };

  // Called for "Select All" checkbox button
  const selectAllHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setAllSelect((pv) => {
      if (pv == 'check') {
        setSelectedItemsTemp([]);
        return '';
      }
      if (pv == '' || pv == 'partialCheck') {
        if (isSearching && searchResults.length > 0) {
          if (searchResults[0].id === NO_RESULTS) {
            return '';
          } else setSelectedItemsTemp([...searchResults]);
        } else setSelectedItemsTemp([...itemData]);
        return 'check';
      }
      return '';
    });
  };

  // Initial state setup
  useEffect(() => {
    setSelectedItems([...defaultSelection]);
    setSelectedItemsTemp([...defaultSelection]);
    setItemData(items);
  }, [items, defaultSelection]);

  // Invoke for temporary selection change
  useEffect(() => {
    if (selectedItemsTemp?.length == itemData?.length) setAllSelect('check');
    else if (selectedItemsTemp?.length > 0) {
      setAllSelect('partialCheck');
    } else setAllSelect('');
  }, [selectedItemsTemp]);

  useEffect(() => {
    if (clearValues) clearAllHandler();
  }, [clearValues]);

  const isItemLoaded = (index: number) => {
    return !hasNextPage || index < itemData?.length;
  };

  const searchItems = async (keyword: string) => {
    if (onSearch) {
      if (keyword.length > 0) {
        setIsSearching(true);
        setSearchTerm(keyword);
      } else {
        setShowSelectAllCheckbox(true);
        //Reset search state
        setIsSearching(false);
        setSearchTerm('');
        setSearchResults([]);
      }
    }
  };

  const loadSearchResults = async (keyword: string) => {
    if (!onSearch) return;

    setSearchResults([]);

    try {
      const newItems = await onSearch(keyword);

      if (newItems.length > 0) {
        setSearchResults([...newItems]);
        setShowSelectAllCheckbox(true);
      } else {
        setSearchResults([{id: NO_RESULTS, name: ''}]);
        setShowSelectAllCheckbox(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  let isPaging = false;
  const loadMoreItems = async (startIndex: number) => {
    if (onPage && !isPaging && !isSearching) {
      isPaging = true;
      try {
        const newItems = await onPage(startIndex, startIndex + itemsPerPage);
        setItemData([...itemData, ...newItems]);
      } finally {
        isPaging = false;
      }
    }
  };

  const getItemData = () => {
    if (isSearching) {
      if (searchResults.length > 0) return searchResults;
      return [];
    } else return itemData;
  };

  const getItemCount = () => {
    if (hasNextPage && !isSearching) {
      return itemData.length + 1;
    }
    if (isSearching) {
      if (searchResults.length > 0) return searchResults.length;
      return 1;
    } else return itemData.length;
  };

  const renderSelectItem = (props: ListChildComponentProps) => {
    const {index, style} = props;

    let component = <></>;
    const item = getItemData()[index];

    if (!isItemLoaded(index) || !item) {
      // Render "Loading..."
      component = (
        <div style={style}>
          <LoadingItem />
        </div>
      );
    } else {
      // Render item
      if (item?.id === NO_RESULTS) {
        component = (
          <div className="select-search-no-results" style={style}>
            {item.name}
          </div>
        );
      } else {
        component = (
          <div style={style}>
            <SelectItem
              key={item[itemKey]}
              itemData={item}
              itemDisplay={itemDisplay}
              onItemClick={selectionChangeHandler}
              isExpanded={isExpanded}
              isSelected={selectedItemsTemp.some(
                (sit) => sit[itemKey] == item[itemKey]
              )}
              showCheckIcon={showCheckIcon}
              maxDropdownItemLength={maxDropdownItemLength}
            />
          </div>
        );
      }
    }

    return component;
  };

  const hasError = () => {
    if (hasValidationError) {
      return true;
    }
    if (fieldState?.error?.message) {
      return true;
    }
    return false;
  };

  const selectAllCondition = useMemo(
    () =>
      (isBoolean(isLoading) && !isLoading && (itemData ?? []).length > 0) ||
      !isBoolean(isLoading),
    [isLoading, itemData]
  );

  const ExpandIconComponent = (props: React.SVGProps<SVGSVGElement>) => (
    <ExpandIcon {...props} aria-hidden="true" />
  );

  return (
    <StyledFormControl
      error={hasError()}
      variant="filled"
      fullWidth
      className={`${styles.multiSelectDropdownInput} ${borderClass}`}
    >
      <span id={hintId} style={{display: 'none'}}>
        {hintText ?? t('common.dropdown.optionSelectHintsText')}
      </span>
      {dropdownLabel && (
        <InputLabel
          id={`aur-multiple-checkbox-label-${labelId}`}
          className={`${disabled ? 'Mui-disabled' : ''}`}
        >
          {dropdownLabel}
        </InputLabel>
      )}
      <Select
        ref={selectComponentRef}
        fullWidth
        multiple
        variant="filled"
        value={selectedItems}
        SelectDisplayProps={{
          'aria-expanded': isDropdownOpen,
          ...(isDropdownOpen && {
            'aria-controls': `${labelId}-listbox`,
          }),
          'aria-label': t('searchableDropdown.multiSelectDropdownAriaLabel', {
            label: dropdownLabel,
          }),
        }}
        MenuProps={{
          ...MenuProps,
          disablePortal: true,
          anchorReference: 'none',
          sx: {
            position: 'absolute',
            height: `${calculatedDropdownHeight}px`,
            ...conditionalPositioningProp,
          },
          TransitionProps: {
            onEnter: calculateDirection,
          },
          id: `${labelId}-listbox`,
          role: 'listbox',
        }}
        IconComponent={ExpandIconComponent}
        labelId={
          dropdownLabel ? `aur-multiple-checkbox-label-${labelId}` : undefined
        }
        disabled={disabled}
        open={isDropdownOpen}
        onBlur={field?.onBlur}
        onOpen={selectOpenHandler}
        onClose={selectCloseHandler}
        data-testid={dataTestId}
        className={isDropdownOpen ? 'drop-down-selected' : ''}
        renderValue={() => (
          <AurSelectedItemsPanel
            selectedItems={selectedItems}
            itemDisplay={itemDisplay}
          />
        )}
        aria-describedby={hintId}
        aria-haspopup="true"
        {...rest}
        onFocus={focusHandler}
        onMouseDown={mouseKeyDownHandler}
      >
        <Box tabIndex={-1} ref={popUpContainerRef}>
          {(search || selectAll) && (
            <Box
              onKeyDown={(e) => {
                if (e.key !== 'Escape') e.stopPropagation();
              }}
            >
              {search && (
                <ListSubheader className="select-search-container">
                  <Box className="select-search-box">
                    <AurSearchBox
                      debounceTimeout={800}
                      minCharForSearch={1}
                      onSearch={searchItems}
                    />
                  </Box>
                </ListSubheader>
              )}
              {selectAll && selectAllCondition && (
                <Box
                  className={`${styles.aurSelectFooterContainer} custom-aur-select-footer-container`}
                  style={{paddingTop: '0px'}}
                >
                  <Grid container>
                    <Grid item xs={12}>
                      <Box
                        onClick={selectAllHandler}
                        sx={{display: showSelectAllCheckbox ? 'block' : 'none'}}
                      >
                        <Checkbox
                          className={styles.aurSelectItemCheckBox}
                          aria-hidden={true}
                          checked={allSelect == 'check'}
                          indeterminate={allSelect == 'partialCheck'}
                          checkedIcon={
                            <img
                              src={
                                mouseEnter
                                  ? '/assests/images/checked_checkbox_hover.svg'
                                  : '/assests/images/checked_checkbox.svg'
                              }
                            />
                          }
                          onMouseEnter={() => setMouseEnter(true)}
                          onMouseLeave={() => setMouseEnter(false)}
                          indeterminateIcon={
                            <img
                              src={
                                mouseEnter
                                  ? '/assests/images/indeterminate_checkbox_hover.svg'
                                  : '/assests/images/indeterminate_checkbox.svg'
                              }
                            />
                          }
                        ></Checkbox>
                        <Typography
                          className={`${styles.aurAllSelectLabel} custom-aur-all-select-label`}
                        >
                          {selectAllLabel}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Box style={{display: showSelectAllCheckbox ? 'block' : 'none'}}>
                <Divider className={`${styles.aurMultiSelectPanelDivider}`} />
              </Box>
            </Box>
          )}

          {isBoolean(isLoading) && isLoading && <LoadingItem />}

          {!isBoolean(isLoading) && (itemData ?? []).length === 0 && (
            <LoadingItem />
          )}

          {itemData?.length > 0 && (
            <Box
              onKeyDown={(e) => {
                if (e.key !== 'Escape') e.stopPropagation();
              }}
            >
              <InfiniteLoader
                isItemLoaded={isItemLoaded}
                itemCount={getItemCount()}
                loadMoreItems={loadMoreItems}
                minimumBatchSize={itemsPerPage}
              >
                {({onItemsRendered, ref}) => (
                  <FixedSizeList
                    onItemsRendered={onItemsRendered}
                    ref={ref}
                    height={
                      showSelectAllCheckbox
                        ? itemHeight * numberOfVisibleItem
                        : 40
                    }
                    itemCount={getItemCount()}
                    itemSize={itemHeight}
                    width={'100%'}
                    className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar}`}
                  >
                    {renderSelectItem}
                  </FixedSizeList>
                )}
              </InfiniteLoader>
              <Box style={{display: showSelectAllCheckbox ? 'block' : 'none'}}>
                <Divider className={`${styles.aurMultiSelectPanelDivider}`} />
              </Box>
              <Box
                className={`${styles.aurSelectFooterContainer} custom-aur-select-footer-container`}
                style={{paddingBottom: '7px'}}
                sx={{display: showSelectAllCheckbox ? 'block' : 'none'}}
              >
                {renderFooter && renderFooter}
                <Grid container>
                  <Grid item xs={6}>
                    <Box className={'left-align-center-y'}>
                      <AurButton
                        classNames="custom-clear-button"
                        type="text"
                        title={t('searchableDropdown.clearAll')}
                        label={t('searchableDropdown.clearAll')}
                        size="Medium"
                        disabled={selectedItemsTemp?.length === 0}
                        onClick={() => clearAllHandler()}
                        dataTestId="Clear-button"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box className={'right-align-center-y'}>
                      <AurButton
                        classNames="custom-apply-button"
                        type="continue"
                        title={submitButtonText}
                        label={`${submitButtonText} ${selectedItemsTemp?.length || ''}`}
                        size="Medium"
                        onClick={() => addClickHandler()}
                        dataTestId="Apply-button"
                        disabled={selectedItemsTemp?.length === 0}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Box>
      </Select>
      <FormHelperText className={fieldState?.error?.message && 'label-error'}>
        {fieldState?.error?.message}
      </FormHelperText>
      <Box className={helperTextClass}>
        <FormHelperText>
          {hasValidationError ? validationText : helperText}
        </FormHelperText>
      </Box>
    </StyledFormControl>
  );
};

export default AurServerSelect;
